import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Upload as UploadIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { articleService, Article } from '../../services/articleService';
import { useNavigate } from 'react-router-dom';

interface SectionDetail {
  text: string;
}

interface ArticleSection {
  title: string;
  icon: string;
  details: SectionDetail[];
  image?: {
    url: string;
    public_id: string;
  };
}

type ArticleCategory = 'immigration-updates' | 'student-visa' | 'work-visa' | 'family-visa';

interface ArticleFormData {
  title: string;
  date: string;
  category: ArticleCategory;
  description: string;
  status: 'draft' | 'published';
  sections: ArticleSection[];
  featuredImage?: {
    url: string;
    public_id: string;
  };
}

const CreateArticle: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [articleData, setArticleData] = useState<ArticleFormData>({
    title: '',
    date: new Date().toISOString().split('T')[0],
    category: 'immigration-updates',
    description: '',
    status: 'draft',
    sections: [],
  });

  const [currentSection, setCurrentSection] = useState<ArticleSection>({
    title: '',
    icon: '',
    details: [],
  });

  const [currentDetail, setCurrentDetail] = useState('');
  const [featuredImage, setFeaturedImage] = useState<File | null>(null);
  const [sectionImage, setSectionImage] = useState<File | null>(null);

  const categories: { value: ArticleCategory; label: string }[] = [
    { value: 'immigration-updates', label: 'Immigration Updates' },
    { value: 'student-visa', label: 'Student Visa' },
    { value: 'work-visa', label: 'Work Visa' },
    { value: 'family-visa', label: 'Family Visa' },
  ];

  const icons = [
    { value: 'Work', label: 'Work Icon' },
    { value: 'School', label: 'School Icon' },
    { value: 'Group', label: 'Family Icon' },
    { value: 'AttachMoney', label: 'Money Icon' },
    { value: 'Language', label: 'Language Icon' },
    { value: 'AccessTime', label: 'Time Icon' },
    { value: 'Home', label: 'Home Icon' },
    { value: 'Business', label: 'Business Icon' },
  ];

  const handleImageUpload = async (file: File, type: 'featured' | 'section') => {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to upload image');

      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Error uploading image:', err);
      throw err;
    }
  };

  const handleAddDetail = () => {
    if (currentDetail.trim()) {
      setCurrentSection({
        ...currentSection,
        details: [...currentSection.details, { text: currentDetail }],
      });
      setCurrentDetail('');
    }
  };

  const handleRemoveDetail = (index: number) => {
    setCurrentSection({
      ...currentSection,
      details: currentSection.details.filter((_, i) => i !== index),
    });
  };

  const handleAddSection = async () => {
    if (currentSection.title && currentSection.icon) {
      let sectionWithImage = { ...currentSection };

      if (sectionImage) {
        try {
          const imageData = await handleImageUpload(sectionImage, 'section');
          sectionWithImage.image = {
            url: imageData.url,
            public_id: imageData.public_id,
          };
        } catch (err) {
          setError('Failed to upload section image');
          return;
        }
      }

      setArticleData({
        ...articleData,
        sections: [...articleData.sections, sectionWithImage],
      });

      setCurrentSection({
        title: '',
        icon: '',
        details: [],
      });
      setSectionImage(null);
    }
  };

  const handleCategoryChange = (event: SelectChangeEvent) => {
    const value = event.target.value as ArticleCategory;
    setArticleData({ ...articleData, category: value });
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    const value = event.target.value as 'draft' | 'published';
    setArticleData({ ...articleData, status: value });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      let finalArticleData: ArticleFormData = { ...articleData };

      if (featuredImage) {
        const imageData = await handleImageUpload(featuredImage, 'featured');
        finalArticleData = {
          ...finalArticleData,
          featuredImage: {
            url: imageData.url,
            public_id: imageData.public_id,
          },
        };
      }

      await articleService.createArticle(finalArticleData);
      navigate('/admin/articles');
    } catch (err) {
      setError('Failed to create article');
      console.error('Error creating article:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <Paper sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <IconButton
              onClick={() => navigate('/admin/articles')}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant="h4"
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Create New Article
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Basic Article Information */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Article Title"
                value={articleData.title}
                onChange={(e) =>
                  setArticleData({ ...articleData, title: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Publication Date"
                value={articleData.date}
                onChange={(e) =>
                  setArticleData({ ...articleData, date: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={articleData.category}
                  label="Category"
                  onChange={handleCategoryChange}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={articleData.status}
                  label="Status"
                  onChange={handleStatusChange}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Article Description"
                value={articleData.description}
                onChange={(e) =>
                  setArticleData({ ...articleData, description: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="featured-image-upload"
                type="file"
                onChange={(e) =>
                  setFeaturedImage(e.target.files ? e.target.files[0] : null)
                }
              />
              <label htmlFor="featured-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                >
                  Upload Featured Image
                </Button>
              </label>
              {featuredImage && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {featuredImage.name}
                </Typography>
              )}
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          {/* Section Builder */}
          <Typography variant="h6" sx={{ mb: 3 }}>
            Add Section
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Section Title"
                value={currentSection.title}
                onChange={(e) =>
                  setCurrentSection({
                    ...currentSection,
                    title: e.target.value,
                  })
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Icon</InputLabel>
                <Select
                  value={currentSection.icon}
                  label="Icon"
                  onChange={(e) =>
                    setCurrentSection({
                      ...currentSection,
                      icon: e.target.value,
                    })
                  }
                >
                  {icons.map((icon) => (
                    <MenuItem key={icon.value} value={icon.value}>
                      {icon.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Add Detail"
                  value={currentDetail}
                  onChange={(e) => setCurrentDetail(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddDetail();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddDetail}
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="section-image-upload"
                type="file"
                onChange={(e) =>
                  setSectionImage(e.target.files ? e.target.files[0] : null)
                }
              />
              <label htmlFor="section-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                >
                  Upload Section Image
                </Button>
              </label>
              {sectionImage && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {sectionImage.name}
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Current Section Preview */}
          {currentSection.details.length > 0 && (
            <Paper sx={{ mt: 3, p: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Current Section Details:
              </Typography>
              <List>
                {currentSection.details.map((detail, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={detail.text} />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveDetail(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddSection}
                sx={{ mt: 2 }}
              >
                Add Section
              </Button>
            </Paper>
          )}

          <Divider sx={{ my: 4 }} />

          {/* Article Sections Preview */}
          {articleData.sections.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Article Sections:
              </Typography>
              {articleData.sections.map((section, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle1">{section.title}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Icon: {section.icon}
                  </Typography>
                  {section.image && (
                    <Typography variant="body2" color="textSecondary">
                      Image: {section.image.url}
                    </Typography>
                  )}
                  <List>
                    {section.details.map((detail, detailIndex) => (
                      <ListItem key={detailIndex}>
                        <ListItemText primary={detail.text} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              ))}
            </Box>
          )}

          {/* Submit Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Article'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default CreateArticle; 